import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { HandsWristsHeroSection } from '../components/HandsWristsHeroSection';
import { ProblemSection } from '../components/ProblemSection';
import { SolutionSection } from '../components/SolutionSection';
import { MechanismSection } from '../components/MechanismSection';
import { BenefitsSection } from '../components/BenefitsSection';
import { UseCasesSection } from '../components/UseCasesSection';
import { HowToUseSection } from '../components/HowToUseSection';
import { TrustSection } from '../components/TrustSection';
import { BundleSection } from '../components/BundleSection';
import { FAQSection } from '../components/FAQSection';
import { CTASection } from '../components/CTASection';
import { PolicyModal } from '../components/PolicyModal';
import { SalesNotification } from '../components/SalesNotification';
import { PageTracker } from '../components/PageTracker';
type PolicyType = 'privacy' | 'terms' | 'refund';
const navigationLinks = [{
  name: 'How It Works',
  href: '#how-it-works'
}, {
  name: 'Benefits',
  href: '#benefits'
}, {
  name: 'How To Use',
  href: '#how-to-use'
}, {
  name: 'Pricing',
  href: '#bundles'
}, {
  name: 'FAQ',
  href: '#faq'
}];
export function HandsWristsPage() {
  const [policyModal, setPolicyModal] = useState<{
    isOpen: boolean;
    type: PolicyType;
  }>({
    isOpen: false,
    type: 'privacy'
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  // Handle scrolling to hash on mount and when hash changes
  useEffect(() => {
    if (location.hash) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          const yOffset = -80;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({
            top: y,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location.hash]);
  const openPolicy = (type: PolicyType) => {
    setPolicyModal({
      isOpen: true,
      type
    });
  };
  const closePolicy = () => {
    setPolicyModal({
      ...policyModal,
      isOpen: false
    });
  };
  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false);
    // Small delay to allow mobile menu to close before scrolling
    setTimeout(() => {
      const element = document.querySelector(href);
      if (element) {
        // Calculate the position accounting for fixed header
        const yOffset = -80;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({
          top: y,
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  return <main className="w-full min-h-screen bg-bone overflow-x-hidden" data-id="element-2127">
      <PageTracker data-id="element-2128" />
      {/* Header */}
      <header className="bg-white md:bg-white/95 md:backdrop-blur-sm border-b border-bone-dark fixed top-0 left-0 right-0 z-50 shadow-sm" data-id="element-2129">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-2130">
          <Link to="/" className="flex items-center" data-id="element-2131">
            <img src="https://cdn.magicpatterns.com/uploads/483EwVYaDBT86YSQWmyKfo/GINEXT_LOGO.png" alt="GINEXT" className="h-6 w-auto" loading="eager" data-id="element-2132" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6" data-id="element-2133">
            {navigationLinks.map(link => <a key={link.name} href={link.href} onClick={e => {
            e.preventDefault();
            handleNavClick(link.href);
          }} className="text-sm font-light text-charcoal hover:text-ginger transition-colors cursor-pointer" data-id="element-2134">
                {link.name}
              </a>)}
          </nav>

          {/* Mobile Menu Button */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-charcoal hover:text-ginger transition-colors" aria-label="Toggle menu" data-id="element-2135">
            {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-2136" /> : <Menu className="w-6 h-6" data-id="element-2137" />}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence data-id="element-2138">
          {mobileMenuOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="md:hidden overflow-hidden border-t border-bone-dark bg-white" data-id="element-2139">
              <nav className="px-4 py-4 space-y-1" data-id="element-2140">
                {navigationLinks.map((link, index) => <motion.div key={link.name} data-id="element-2141">
                    <a href={link.href} onClick={e => {
                e.preventDefault();
                setMobileMenuOpen(false);
                handleNavClick(link.href);
              }} className="block py-3 px-4 text-base font-light text-charcoal hover:text-ginger hover:bg-bone/50 rounded-lg transition-all cursor-pointer" data-id="element-2142">
                      {link.name}
                    </a>
                  </motion.div>)}
              </nav>
            </motion.div>}
        </AnimatePresence>
      </header>

      {/* Add padding to account for fixed header */}
      <div className="pt-[57px]" data-id="element-2143">
        <HandsWristsHeroSection data-id="element-2144" />
        <ProblemSection data-id="element-2145" />
        <SolutionSection data-id="element-2146" />
        <div id="how-it-works" className="scroll-mt-20" data-id="element-2147">
          <MechanismSection data-id="element-2148" />
        </div>
        <div id="benefits" className="scroll-mt-20" data-id="element-2149">
          <BenefitsSection beforeImage="https://cdn.magicpatterns.com/uploads/eN7H1vGhxVWCsMHWiskT91/u8896349413_Photorealistic_close-up_image_of_a_55-year-old_wo_f572f95f-228f-4d87-b24d-93532fe478fc_3.png" afterImage="https://cdn.magicpatterns.com/uploads/73FK8r7UE1dp9V3CjsjuyV/u8896349413_Photorealistic_close-up_image_of_the_same_55-year_1129f857-1a27-484e-b233-351e63e03edc_3.png" data-id="element-2150" />
        </div>
        <UseCasesSection data-id="element-2151" />
        <div id="how-to-use" className="scroll-mt-20" data-id="element-2152">
          <HowToUseSection data-id="element-2153" />
        </div>
        <TrustSection data-id="element-2154" />
        <div id="bundles" className="scroll-mt-20" data-id="element-2155">
          <BundleSection data-id="element-2156" />
        </div>
        <div id="faq" className="scroll-mt-20" data-id="element-2157">
          <FAQSection data-id="element-2158" />
        </div>
        <CTASection data-id="element-2159" />
      </div>

      {/* Footer */}
      <footer className="bg-charcoal text-gray-400 py-4 text-center text-xs border-t border-white/10" data-id="element-2160">
        <div className="max-w-6xl mx-auto px-4" data-id="element-2161">
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-3" data-id="element-2162">
            <button onClick={() => openPolicy('privacy')} className="hover:text-ginger transition-colors underline" data-id="element-2163">
              Privacy Policy
            </button>
            <button onClick={() => openPolicy('terms')} className="hover:text-ginger transition-colors underline" data-id="element-2164">
              Terms of Service
            </button>
            <button onClick={() => openPolicy('refund')} className="hover:text-ginger transition-colors underline" data-id="element-2165">
              Refund Policy
            </button>
          </div>
          <p data-id="element-2166">© {new Date().getFullYear()} GINEXT. All rights reserved.</p>
          <p className="mt-1" data-id="element-2167">
            These statements have not been evaluated by the Food and Drug
            Administration. This product is not intended to diagnose, treat,
            cure, or prevent any disease.
          </p>
        </div>
      </footer>

      <PolicyModal isOpen={policyModal.isOpen} onClose={closePolicy} type={policyModal.type} data-id="element-2168" />

      {/* Sales Notification */}
      <SalesNotification data-id="element-2169" />
    </main>;
}